import * as THREE from 'three';
import { animate } from 'animejs';
import { resolveCircleWalls, segmentVsAabbs } from '../../engine/collision.js';
import {
  animateEnemyDeath,
  animateEnemyHit,
  animateEnemyOutlineHide,
  animateEnemyOutlinePopup,
  animateSpawnIntro,
  killObjectTweens,
} from '../ui/animator.js';

// Base enemy: procedural voxel body, shared state machine.
// Subclasses override `buildMesh()` and `updateAI()`.

const NAV_REPATH_INTERVAL = 0.24;
const NAV_GOAL_REFRESH_DIST = 1.15;
const NAV_WAYPOINT_RADIUS = 0.55;
const NAV_SAMPLE_STEP = 0.65;
const NAV_MAX_VISITS = 1400;

function distSqPointAabb(x, z, w) {
  const nx = Math.max(w.minX, Math.min(x, w.maxX));
  const nz = Math.max(w.minZ, Math.min(z, w.maxZ));
  const dx = x - nx;
  const dz = z - nz;
  return dx * dx + dz * dz;
}

export class BaseEnemy {
  constructor({ scene, x, z, stats, audio }) {
    this.scene = scene;
    this.audio = audio;
    this.stats = stats;
    this.hp = stats.hp;
    this.maxHp = stats.hp;
    this.alive = true;
    this.radius = stats.radius;
    this.x = x;
    this.z = z;
    this.vx = 0;
    this.vz = 0;
    this.aimX = 0;
    this.aimZ = 1;
    this.state = 'idle';
    this.stateTime = 0;
    this.fireCd = 0;
    this.hitFlash = 0;
    this.spawnX = x;
    this.spawnZ = z;
    this.alertTimer = 0;
    this.lastKnownPlayerX = x;
    this.lastKnownPlayerZ = z;
    this._roamTarget = null;
    this._roamPause = 0;
    this._path = [];
    this._pathIndex = 0;
    this._pathGoalX = x;
    this._pathGoalZ = z;
    this._pathTimer = 0;
    this._pathFailedTimer = 0;
    this._lastCanSeePlayer = false;
    this._coneHalfAngle = ((stats.viewConeAngleDeg ?? 70) * Math.PI) / 180 / 2;
    this._coneCosThreshold = Math.cos(this._coneHalfAngle);
    this._readyToDispose = false;
    this._outlineActive = false;
    this._outlineEmphasis = 0;
    this.isBoss = false;
    this._motionState = {
      yawOffset: 0,
      posePitch: 0,
      inertiaPitch: 0,
      hitOffsetX: 0,
      hitOffsetZ: 0,
    };
    this._inertiaPitchVel = 0;
    this._idleSnapAnim = null;
    this._poseAnim = null;
    this._hitAnim = null;
    this._wasMoving = false;
    this._idleSnapCd = 0.4 + Math.random() * 0.7;
    this._pendingHitDx = null;
    this._pendingHitDz = null;
    this._spawnOffsetY = 0;

    this.group = new THREE.Group();
    this.group.userData.enemyRef = this;
    this.group.position.set(x, 0, z);
    this.buildMesh();
    this._buildVisionCone();
    this._buildTargetOutline();
    scene.add(this.group);
    animateSpawnIntro(this.group);
  }

  buildMesh() {
    const st = this.stats;
    const mat = new THREE.MeshStandardMaterial({ color: st.bodyColor, roughness: 0.95 });
    const matAcc = new THREE.MeshStandardMaterial({
      color: st.accentColor,
      emissive: st.accentColor,
      emissiveIntensity: 1.2,
    });
    this._matBody = mat;

    const body = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.9, 0.5), mat);
    body.position.y = 0.9;
    body.castShadow = true;
    this.group.add(body);

    const head = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.55, 0.55), mat);
    head.position.y = 1.62;
    head.castShadow = true;
    this.group.add(head);

    const visor = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.1, 0.08), matAcc);
    visor.position.set(0, 1.66, 0.28);
    this.group.add(visor);

    const legL = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.5, 0.38), mat);
    legL.position.set(-0.2, 0.25, 0); legL.castShadow = true; this.group.add(legL);
    const legR = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.5, 0.38), mat);
    legR.position.set(0.2, 0.25, 0); legR.castShadow = true; this.group.add(legR);

    this._head = head;
  }

  hasLineOfSight(targetX, targetZ, walls) {
    const t = segmentVsAabbs(this.x, this.z, targetX, targetZ, walls);
    return t === null;
  }

  distTo(tx, tz) {
    return Math.hypot(tx - this.x, tz - this.z);
  }

  _buildVisionCone() {
    const range = this.stats.sightRange ?? 10;
    const half = this._coneHalfAngle;
    const shape = new THREE.Shape();
    shape.moveTo(0, 0);
    const steps = 20;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const a = -half + t * (half * 2);
      shape.lineTo(Math.sin(a) * range, Math.cos(a) * range);
    }
    shape.lineTo(0, 0);

    const geo = new THREE.ShapeGeometry(shape);
    geo.rotateX(Math.PI / 2);
    const mat = new THREE.MeshBasicMaterial({
      color: 0xff2d6b,
      transparent: true,
      opacity: 0.08,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    this._visionCone = new THREE.Mesh(geo, mat);
    this._visionCone.position.y = 0.04;
    this._visionCone.renderOrder = 4;
    this._visionConeBaseColor = new THREE.Color();
    this._visionConeBaseOpacity = 0.08;
    this.group.add(this._visionCone);
  }

  _buildTargetOutline() {
    const mat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      side: THREE.BackSide,
      depthWrite: false,
      toneMapped: false,
    });
    this._targetOutlineScale = 1.09;
    this._targetOutlineNodes = [];

    const sourceMeshes = [];
    this.group.traverse((c) => {
      if (c === this._visionCone) return;
      if (!c?.isMesh) return;
      sourceMeshes.push(c);
    });

    for (const src of sourceMeshes) {
      let outlineMesh = null;
      if (src.isSkinnedMesh) {
        outlineMesh = new THREE.SkinnedMesh(src.geometry, mat);
        outlineMesh.bind(src.skeleton, src.bindMatrix);
      } else {
        outlineMesh = new THREE.Mesh(src.geometry, mat);
      }
      outlineMesh.name = `${src.name || 'enemyMesh'}_outline`;
      outlineMesh.visible = true;
      outlineMesh.castShadow = false;
      outlineMesh.receiveShadow = false;
      outlineMesh.frustumCulled = false;
      outlineMesh.renderOrder = 9;
      outlineMesh.scale.setScalar(this._targetOutlineScale);
      outlineMesh.userData.isTargetOutline = true;
      outlineMesh.userData.outlineBaseScale = this._targetOutlineScale;
      outlineMesh.raycast = () => {};
      src.add(outlineMesh);
      this._targetOutlineNodes.push(outlineMesh);
    }

    this._targetOutline = this._targetOutlineNodes;
    this._targetOutlineMat = mat;
  }

  setTargetOutlineActive(active, emphasis = 0) {
    if (!this._targetOutline || !this._targetOutlineMat) return;
    const nextEmphasis = Math.max(0, Math.min(1, emphasis));
    if (active && this._outlineActive && Math.abs(this._outlineEmphasis - nextEmphasis) < 0.05) return;
    if (!active && !this._outlineActive) return;

    this._outlineActive = active;
    this._outlineEmphasis = nextEmphasis;

    if (active) {
      animateEnemyOutlinePopup(this._targetOutline, this._targetOutlineMat, { intensity: nextEmphasis });
      return;
    }

    animateEnemyOutlineHide(this._targetOutline, this._targetOutlineMat);
  }

  canSeePlayer(playerX, playerZ, walls) {
    const dx = playerX - this.x;
    const dz = playerZ - this.z;
    const d = Math.hypot(dx, dz);
    if (d > this.stats.sightRange || d < 0.0001) return false;
    if (!this.hasLineOfSight(playerX, playerZ, walls)) return false;
    const nx = dx / d;
    const nz = dz / d;
    const dot = nx * this.aimX + nz * this.aimZ;
    return dot >= this._coneCosThreshold;
  }

  sensePlayer(game, dt) {
    const player = game.player;
    if (!player.alive) return false;
    if (game?.isPlayerHiddenFromEnemies?.()) {
      this.alertTimer = 0;
      this._lastCanSeePlayer = false;
      return false;
    }
    this.alertTimer = Math.max(0, this.alertTimer - dt);
    const visible = this.canSeePlayer(player.x, player.z, game.room.walls);
    if (visible) {
      this.alertTimer = Math.max(this.alertTimer, this.stats.alertMemory ?? 1.8);
      this.lastKnownPlayerX = player.x;
      this.lastKnownPlayerZ = player.z;
    }
    this._lastCanSeePlayer = visible;
    return visible || this.alertTimer > 0;
  }

  notifyPlayerDetected(playerX, playerZ, duration = 2.5) {
    this.lastKnownPlayerX = playerX;
    this.lastKnownPlayerZ = playerZ;
    this.alertTimer = Math.max(this.alertTimer, duration);
  }

  roam(dt, walls) {
    const roamRadius = this.stats.roamRadius ?? 4.5;
    const arriveRadius = this.stats.roamArriveRadius ?? 0.6;
    if (this._roamPause > 0) {
      this._roamPause -= dt;
      return;
    }

    if (!this._roamTarget) {
      const a = Math.random() * Math.PI * 2;
      const r = Math.random() * roamRadius;
      this._roamTarget = {
        x: this.spawnX + Math.cos(a) * r,
        z: this.spawnZ + Math.sin(a) * r,
      };
    }

    const d = this.distTo(this._roamTarget.x, this._roamTarget.z);
    if (d <= arriveRadius) {
      this._roamTarget = null;
      this._roamPause = 0.3 + Math.random() * 0.8;
      return;
    }
    this.moveToward(this._roamTarget.x, this._roamTarget.z, dt, walls, this.stats.roamSpeedMul ?? 0.55);
  }

  pursuePlayer(game, dt, speedMul = 1, options = {}) {
    const player = game.player;
    if (!player?.alive) return;
    this.sensePlayer(game, dt);
    this.lastKnownPlayerX = player.x;
    this.lastKnownPlayerZ = player.z;
    this.moveWithPathfinding(player.x, player.z, dt, game, speedMul, options);
  }

  moveWithPathfinding(tx, tz, dt, game, speedMul = 1, options = {}) {
    const room = game?.room;
    const walls = room?.walls || [];
    const dx = tx - this.x;
    const dz = tz - this.z;
    const d = Math.hypot(dx, dz);
    if (d < 0.0001) return;

    if (this._hasClearMove(this.x, this.z, tx, tz, room, walls)) {
      this._path.length = 0;
      this._pathIndex = 0;
      this.moveToward(tx, tz, dt, walls, speedMul);
      return;
    }

    this._pathTimer -= dt;
    this._pathFailedTimer = Math.max(0, this._pathFailedTimer - dt);
    const goalMoved = Math.hypot(tx - this._pathGoalX, tz - this._pathGoalZ) > NAV_GOAL_REFRESH_DIST;
    if ((!this._path.length || goalMoved || this._pathTimer <= 0) && this._pathFailedTimer <= 0) {
      const pathfindStart = performance.now();
      this._path = this._findPathTo(tx, tz, room, walls);
      const pathfindSection = game?._perf?.sections?.pathfind;
      if (pathfindSection) {
        pathfindSection.ms += performance.now() - pathfindStart;
        pathfindSection.calls += 1;
      }
      this._pathIndex = 0;
      this._pathGoalX = tx;
      this._pathGoalZ = tz;
      this._pathTimer = options.repathInterval ?? NAV_REPATH_INTERVAL;
      if (!this._path.length) this._pathFailedTimer = 0.35;
    }

    const waypoint = this._nextPathWaypoint(tx, tz, room, walls);
    if (waypoint) {
      this.moveToward(waypoint.x, waypoint.z, dt, walls, speedMul);
      return;
    }

    this._steerAroundBlockedTarget(tx, tz, dt, walls, speedMul);
  }

  _nextPathWaypoint(tx, tz, room, walls) {
    while (this._pathIndex < this._path.length) {
      const p = this._path[this._pathIndex];
      if (this.distTo(p.x, p.z) > NAV_WAYPOINT_RADIUS) break;
      this._pathIndex++;
    }

    for (let i = this._path.length - 1; i >= this._pathIndex + 1; i--) {
      const p = this._path[i];
      if (this._hasClearMove(this.x, this.z, p.x, p.z, room, walls)) {
        this._pathIndex = i;
        break;
      }
    }

    return this._path[this._pathIndex] || (this._hasClearMove(this.x, this.z, tx, tz, room, walls) ? { x: tx, z: tz } : null);
  }

  _findPathTo(tx, tz, room, walls) {
    const nav = this._getNavigationGrid(room, walls);
    if (!nav?.cells?.length) return [];

    const start = this._nearestNavCell(nav, this.x, this.z);
    const goal = this._nearestNavCell(nav, tx, tz);
    if (!start || !goal) return [];
    if (start.key === goal.key) return [{ x: tx, z: tz }];

    const open = [{ key: start.key, f: 0 }];
    const cameFrom = new Map();
    const gScore = new Map([[start.key, 0]]);
    const closed = new Set();
    let visits = 0;

    while (open.length && visits++ < NAV_MAX_VISITS) {
      let bestIndex = 0;
      for (let i = 1; i < open.length; i++) {
        if (open[i].f < open[bestIndex].f) bestIndex = i;
      }
      const currentKey = open.splice(bestIndex, 1)[0].key;
      if (currentKey === goal.key) return this._reconstructPath(cameFrom, currentKey, nav, tx, tz);
      if (closed.has(currentKey)) continue;
      closed.add(currentKey);

      const current = nav.byKey.get(currentKey);
      for (const nKey of current.neighbors) {
        if (closed.has(nKey)) continue;
        const n = nav.byKey.get(nKey);
        const stepCost = Math.hypot(n.x - current.x, n.z - current.z);
        const tentative = (gScore.get(currentKey) ?? Infinity) + stepCost;
        if (tentative >= (gScore.get(nKey) ?? Infinity)) continue;
        cameFrom.set(nKey, currentKey);
        gScore.set(nKey, tentative);
        const h = Math.hypot(n.x - goal.x, n.z - goal.z);
        open.push({ key: nKey, f: tentative + h });
      }
    }

    return [];
  }

  _reconstructPath(cameFrom, currentKey, nav, tx, tz) {
    const reversed = [];
    let key = currentKey;
    while (key) {
      const c = nav.byKey.get(key);
      if (c) reversed.push({ x: c.x, z: c.z });
      key = cameFrom.get(key);
    }
    const path = reversed.reverse();
    if (path.length > 1) path.shift();
    path.push({ x: tx, z: tz });
    return path;
  }

  _nearestNavCell(nav, x, z) {
    let best = null;
    let bestD2 = Infinity;
    for (const cell of nav.cells) {
      const dx = cell.x - x;
      const dz = cell.z - z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) {
        best = cell;
        bestD2 = d2;
      }
    }
    return best;
  }

  _getNavigationGrid(room, walls) {
    if (!room) return null;
    const clearance = Math.max(0.25, this.radius + 0.08);
    const navRevision = Number.isFinite(room._navRevision) ? room._navRevision : 0;
    const cacheKey = `enemyNav:${clearance.toFixed(2)}:${walls.length}:${navRevision}`;
    if (!(room._enemyNavCache instanceof Map)) room._enemyNavCache = new Map();
    const cached = room._enemyNavCache.get(cacheKey);
    if (cached) return cached;

    const rawCells = this._collectNavigationCells(room);
    const cells = [];
    const byKey = new Map();
    for (const raw of rawCells) {
      const key = `${raw.cx},${raw.cy}`;
      if (!this._canOccupy(raw.x, raw.z, room, walls, clearance)) continue;
      const cell = { ...raw, key, neighbors: [] };
      cells.push(cell);
      byKey.set(key, cell);
    }

    const dirs = [
      [1, 0], [-1, 0], [0, 1], [0, -1],
      [1, 1], [-1, 1], [1, -1], [-1, -1],
    ];
    for (const cell of cells) {
      for (const [dx, dz] of dirs) {
        const key = `${cell.cx + dx},${cell.cy + dz}`;
        const neighbor = byKey.get(key);
        if (!neighbor) continue;
        if (dx !== 0 && dz !== 0) {
          if (!byKey.has(`${cell.cx + dx},${cell.cy}`) || !byKey.has(`${cell.cx},${cell.cy + dz}`)) continue;
        }
        if (!this._hasClearMove(cell.x, cell.z, neighbor.x, neighbor.z, room, walls, clearance)) continue;
        cell.neighbors.push(key);
      }
    }

    const nav = { cells, byKey };
    room._enemyNavCache.set(cacheKey, nav);
    return nav;
  }

  _collectNavigationCells(room) {
    if (Array.isArray(room._walkableCells) && room._walkableCells.length) {
      return room._walkableCells;
    }
    if (Array.isArray(room.template) && typeof room._cellToWorld === 'function') {
      const cells = [];
      for (let cy = 0; cy < room.template.length; cy++) {
        const row = room.template[cy];
        for (let cx = 0; cx < row.length; cx++) {
          if (row[cx] === 'W') continue;
          cells.push({ cx, cy, ...room._cellToWorld(cx, cy) });
        }
      }
      return cells;
    }
    return [];
  }

  _hasClearMove(sx, sz, ex, ez, room, walls, clearance = this.radius + 0.08) {
    const dist = Math.hypot(ex - sx, ez - sz);
    const steps = Math.max(1, Math.ceil(dist / NAV_SAMPLE_STEP));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = sx + (ex - sx) * t;
      const z = sz + (ez - sz) * t;
      if (!this._canOccupy(x, z, room, walls, clearance)) return false;
    }
    return true;
  }

  _canOccupy(x, z, room, walls, clearance) {
    const hasWalkableGrid = (room?._walkable?.size ?? 0) > 0 || Array.isArray(room?.template);
    if (hasWalkableGrid && room?.isWalkableSpawn && !room.isWalkableSpawn(x, z, Math.max(0, clearance - 0.05))) return false;
    for (const w of walls) {
      if (distSqPointAabb(x, z, w) <= clearance * clearance) return false;
    }
    return true;
  }

  _steerAroundBlockedTarget(tx, tz, dt, walls, speedMul) {
    const dx = tx - this.x;
    const dz = tz - this.z;
    const len = Math.hypot(dx, dz) || 1;
    const nx = dx / len;
    const nz = dz / len;
    const side = this.stateTime % 1.2 < 0.6 ? 1 : -1;
    const steerX = nx * 1.2 + -nz * side * 1.8;
    const steerZ = nz * 1.2 + nx * side * 1.8;
    this.moveToward(this.x + steerX, this.z + steerZ, dt, walls, speedMul * 0.82);
  }

  _updateVisionCone(game) {
    if (!this._visionCone) return;
    const mat = this._visionCone.material;
    if (this._lastCanSeePlayer) {
      this._visionConeBaseColor.setHex(0xff4060);
      this._visionConeBaseOpacity = 0.2;
    } else if (this.alertTimer > 0) {
      this._visionConeBaseColor.setHex(0xffaa33);
      this._visionConeBaseOpacity = 0.14;
    } else {
      this._visionConeBaseColor.setHex(0xff2d6b);
      this._visionConeBaseOpacity = 0.08;
    }

    // Sample illumination near the cone's centroid so cones that straddle
    // the player's light bubble dim smoothly instead of popping.
    const sampleX = this.x + this.aimX * 2.0;
    const sampleZ = this.z + this.aimZ * 2.0;
    const illum = game?.illuminationAt ? game.illuminationAt(sampleX, sampleZ) : 1;
    mat.color.copy(this._visionConeBaseColor).multiplyScalar(illum);
    mat.opacity = this._visionConeBaseOpacity * (0.25 + 0.75 * illum);
  }

  takeDamage(n, game) {
    if (!this.alive) return false;
    if (!this.isBoss) this._playHitKnockbackAnim();
    const dmgMul = this.stats.damageTakenMul ?? 1;
    const appliedDamage = Math.max(0.2, n * dmgMul);
    this.hp -= appliedDamage;
    this.hitFlash = 0.1;
    animateEnemyHit(this.group, this._matBody);
    if (this.hp <= 0) {
      this.alive = false;
      animateEnemyDeath(this.group, () => {
        this._readyToDispose = true;
      });
      if (this.audio) {
        this.audio.play('enemyDeath', { rate: 0.98 + Math.random() * 0.06 });
        this.audio.play('enemyDeathPunch', { rate: 0.9 + Math.random() * 0.08 });
      }
      game?.onEnemyKilled(this);
      return true;
    }
    return false;
  }

  updateHitFlash(dt) {
    if (!this.alive) return;
    if (this.hitFlash > 0) {
      this.hitFlash -= dt;
      this._matBody.emissive.setHex(0xffffff);
      this._matBody.emissiveIntensity = 0.6;
    } else if (this._matBody.emissiveIntensity !== 0) {
      this._matBody.emissive.setHex(0x000000);
      this._matBody.emissiveIntensity = 0;
    }
  }

  moveToward(tx, tz, dt, walls, speedMul = 1) {
    const dx = tx - this.x, dz = tz - this.z;
    const len = Math.hypot(dx, dz);
    if (len < 0.0001) return;
    const nx = dx / len, nz = dz / len;
    this.vx = nx * this.stats.moveSpeed * speedMul;
    this.vz = nz * this.stats.moveSpeed * speedMul;
    const resolved = resolveCircleWalls(
      this.x + this.vx * dt,
      this.z + this.vz * dt,
      this.radius,
      walls
    );
    this.x = resolved.x;
    this.z = resolved.z;
    this.aimX = nx;
    this.aimZ = nz;
  }

  faceAim() {
    this.group.rotation.y = Math.atan2(this.aimX, this.aimZ) + this._motionState.yawOffset;
  }

  queueHitDirection(dx, dz) {
    const len = Math.hypot(dx, dz) || 1;
    this._pendingHitDx = dx / len;
    this._pendingHitDz = dz / len;
  }

  _playIdleSnapAnim() {
    if (this._idleSnapAnim) this._idleSnapAnim.cancel();
    const dir = Math.random() < 0.5 ? -1 : 1;
    const snap = dir * 0.16;
    const overshoot = -dir * 0.06;
    this._idleSnapAnim = animate(this._motionState, {
      yawOffset: [
        { to: snap, duration: 68, ease: 'outQuad' },
        { to: overshoot, duration: 118, ease: 'outBack(2.1)' },
        { to: 0, duration: 96, ease: 'outCubic' },
      ],
    });
  }

  _cancelPoseAnim() {
    if (this._poseAnim) {
      this._poseAnim.cancel();
      this._poseAnim = null;
    }
  }

  _cancelHitAnim() {
    if (this._hitAnim) {
      this._hitAnim.cancel();
      this._hitAnim = null;
    }
  }

  _playStopSettleAnim() {
    this._cancelPoseAnim();
    this._poseAnim = animate(this._motionState, {
      posePitch: [
        { to: 0.08, duration: 100, ease: 'outQuad' },
        { to: -0.04, duration: 130, ease: 'outBack(2.3)' },
        { to: 0, duration: 100, ease: 'outCubic' },
      ],
    });
  }

  playFireRecoilAnim() {
    if (this.isBoss || !this.alive) return;
    this._cancelPoseAnim();
    this._poseAnim = animate(this._motionState, {
      posePitch: [
        { to: -0.16, duration: 80, ease: 'outQuad' },
        {
          to: 0.08,
          duration: 140,
          ease: 'linear(0, 0.0136, 0.0506, 0.1058, 0.1743, 0.2518, 0.3346, 0.4196, 0.5041, 0.586, 0.6636, 0.7357, 0.8014, 0.8601, 0.9117, 0.956, 0.9932, 1.0237, 1.048, 1.0665, 1.0798, 1.0885, 1.0933, 1.0948, 1.0935, 1.0899, 1.0847, 1.0782, 1.0708, 1.063, 1.0549, 1.0469, 1.0392, 1.0318, 1.025, 1.0188, 1.0132, 1.0083, 1.0041, 1.0006, 0.9977, 0.9954, 0.9937, 0.9924, 0.9916, 0.9912, 0.991, 0.9911, 0.9915, 0.992, 0.9926, 0.9933, 0.994, 0.9948, 0.9956, 0.9963, 0.997, 0.9976, 0.9982, 0.9988, 0.9992, 0.9996, 0.9999, 1.0002, 1.0004, 1.0006, 1.0007, 1.0008, 1.0008, 1.0009, 1.0008, 1.0008, 1.0008, 1.0007, 1.0006, 1.0006, 1.0005, 1.0004, 1.0004, 1.0003, 1.0002, 1.0002, 1.0001, 1.0001, 1, 1, 1, 1, 0.9999, 0.9999, 0.9999, 0.9999, 0.9999, 0.9999, 0.9999, 0.9999, 0.9999, 0.9999, 0.9999, 1, 1)',
        },
        { to: 0, duration: 110, ease: 'outCubic' },
      ],
    });
  }

  _playHitKnockbackAnim() {
    let dx = this._pendingHitDx;
    let dz = this._pendingHitDz;
    this._pendingHitDx = null;
    this._pendingHitDz = null;
    if (!Number.isFinite(dx) || !Number.isFinite(dz)) {
      dx = -this.aimX;
      dz = -this.aimZ;
    }
    const len = Math.hypot(dx, dz) || 1;
    dx /= len;
    dz /= len;
    this._cancelHitAnim();
    this._hitAnim = animate(this._motionState, {
      keyframes: [
        {
          hitOffsetX: dx * 0.46,
          hitOffsetZ: dz * 0.46,
          duration: 100,
          ease: 'outQuad',
        },
        {
          hitOffsetX: 0,
          hitOffsetZ: 0,
          duration: 200,
          ease: 'outBack(2.25)',
        },
      ],
    });
  }

  _applyComposedPitch() {
    this.group.rotation.x = this._motionState.inertiaPitch + this._motionState.posePitch;
  }

  _updateRobotMotion(dt, moveSpeed) {
    if (this.isBoss) return;
    const moving = moveSpeed > 0.2;
    const baseSpeed = Math.max(0.001, this.stats.moveSpeed ?? 1);
    const speedNorm = THREE.MathUtils.clamp(moveSpeed / baseSpeed, 0, 1.6);
    const targetPull = moving ? -THREE.MathUtils.clamp(0.02 + speedNorm * 0.06, 0.02, 0.11) : 0;
    const spring = moving ? 30 : 22;
    const damping = moving ? 10 : 8;
    const accel = (targetPull - this._motionState.inertiaPitch) * spring - this._inertiaPitchVel * damping;
    this._inertiaPitchVel += accel * dt;
    this._motionState.inertiaPitch += this._inertiaPitchVel * dt;
    this._motionState.inertiaPitch = THREE.MathUtils.clamp(this._motionState.inertiaPitch, -0.14, 0.08);
    if (!moving && Math.abs(this._motionState.inertiaPitch) < 0.0004 && Math.abs(this._inertiaPitchVel) < 0.002) {
      this._motionState.inertiaPitch = 0;
      this._inertiaPitchVel = 0;
    }

    if (this._wasMoving && !moving) this._playStopSettleAnim();
    this._wasMoving = moving;
    if (moving) {
      this._idleSnapCd = 0.35 + Math.random() * 0.4;
      return;
    }
    this._idleSnapCd -= dt;
    if (this._idleSnapCd <= 0) {
      this._playIdleSnapAnim();
      this._idleSnapCd = 0.45 + Math.random() * 0.8;
    }
  }

  update(dt, game, options = {}) {
    if (!this.alive) return;
    const skipPresentation = options?.skipPresentation === true;
    this.stateTime += dt;
    this.fireCd = Math.max(0, this.fireCd - dt);
    const prevX = this.x;
    const prevZ = this.z;
    const airborne = this._spawnOffsetY > 0.02;
    if (!airborne) {
      this.updateAI(dt, game);
    } else {
      // Hold combat behavior until landing so enemies cannot shoot mid-air.
      this.state = 'spawning';
      this.vx = 0;
      this.vz = 0;
    }
    const moveSpeed = dt > 0 ? Math.hypot(this.x - prevX, this.z - prevZ) / dt : 0;
    if (!skipPresentation) this._updateRobotMotion(dt, moveSpeed);
    this.updateHitFlash(dt);
    if (!skipPresentation) this._updateVisionCone(game);
    this.group.position.set(
      this.x + this._motionState.hitOffsetX,
      this._spawnOffsetY,
      this.z + this._motionState.hitOffsetZ
    );
    this.faceAim();
    this._applyComposedPitch();
  }

  canDispose() {
    return !this.alive && this._readyToDispose;
  }

  dispose() {
    this.setTargetOutlineActive(false, 0);
    if (this._idleSnapAnim) this._idleSnapAnim.cancel();
    this._cancelPoseAnim();
    this._cancelHitAnim();
    killObjectTweens(this.group);
    this.group.traverse((c) => {
      if (c.material) {
        if (Array.isArray(c.material)) c.material.forEach((m) => killObjectTweens(m));
        else killObjectTweens(c.material);
      }
    });
    this.scene.remove(this.group);
    this.group.traverse((c) => {
      if (c.geometry) c.geometry.dispose?.();
      if (Array.isArray(c.material)) c.material.forEach((m) => m?.dispose?.());
      else c.material?.dispose?.();
    });
  }

  updateAI(_dt, _game) {}
}
